import { useEffect, useMemo } from 'react';
import { isTauri } from '../../utils/tauri';
import { useFtpStore } from '../../store/ftpStore';
import { useConnectionRows } from '../../hooks/useConnectionRows';
import { useProjectFilterScope } from '../../scope/projectMembership';
import { sortProjectFirst } from '../../scope/projectScopeFilter';
import { KIND_SSH, connectionNodeId } from '../../scope/connectionProjectGroups';

/**
 * The SSH connections a terminal can open — the user's own vault's `sftp`
 * items and every team vault they hold a grant in — plus the vault's lock
 * state. Shared by the connection modal (start screen) and the "SSH
 * connection" submenu of the "+" / "Split with…" menus.
 *
 * SSH only: an FTP/FTPS row has no shell behind it, and Rust refuses one with
 * "not an SSH-capable connection" — for team rows too, in the same words.
 *
 * `active` false skips the load (a closed modal must not hit the vault).
 */
export function useSshConnections(active = true) {
  const { rows } = useConnectionRows();
  const vaultLocked = useFtpStore((s) => s.vaultLocked);
  const unlocking = useFtpStore((s) => s.unlocking);

  useEffect(() => {
    if (active && isTauri()) useFtpStore.getState().loadConnections();
  }, [active]);

  // Project scope (card 5.4): the active project's hosts first, in every picker.
  const projectScope = useProjectFilterScope();
  const connections = useMemo(
    () =>
      sortProjectFirst(
        rows.filter((c) => String(c.index?.protocol || 'sftp').toLowerCase() === 'sftp'),
        projectScope,
        (c) => ({ nodeId: connectionNodeId(KIND_SSH, c) })
      ),
    [rows, projectScope]
  );

  return {
    connections,
    vaultLocked,
    unlocking,
    unlock: () => useFtpStore.getState().unlockVault(),
  };
}

/** `user@host:port` under a connection's name, or '' when the host is unknown. */
export function connectionSubtitle(conn) {
  const ix = conn?.index || {};
  if (!ix.host) return '';
  return `${ix.username ? `${ix.username}@` : ''}${ix.host}${ix.port ? `:${ix.port}` : ''}`;
}
